import * as _ from "lodash"
import {
    WB_CBORow,
    WB_CBOSetPredictorRow,
    WB_CBOSetRow,
    WB_ModelEventTypeRelationshipRow,
    WB_ModelVolumeComponentRelationshipRow,
    WB_ScenarioPlannerPeriodPartitionRow,
    WB_ScenarioPlannerRow,
    WB_ScenarioPlannerTrialEventRow,
    WB_ScenarioPlannerTrialPRRow,
    WB_ScenarioPlannerTrialRow,
    WB_ScenarioPlanRow,
    WB_SimulationPredictorRelationshipRow,
    WB_SimulationRow,
    WB_SimulationSpendMetricsRow,
    WB_SpendFunctionRelationshipRow
} from "../PA.Core/PAdbSchemaRows";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import {
    esWB_CBO,
    esWB_CBO_Constraint,
    esWB_CBO_Input_Relationship,
    esWB_CBO_Set,
    esWB_CBO_Set_Item,
    esWB_CBO_Set_Predictor, esWB_Entity, esWB_Model,
    esWB_Model_Event_Type_Relationship,
    esWB_Model_Predictor,
    esWB_Model_Profile,
    esWB_Model_Profile_Group,
    esWB_Model_Profile_Group_Predictor,
    esWB_Model_Volume_ComponentR,
    esWB_Published_Model,
    esWB_ScnPlan,
    esWB_ScnPlanner,
    esWB_ScnPlannerCalc, esWB_ScnPlannerCnstr, esWB_ScnPlannerCR, esWB_ScnPlannerESR,
    esWB_ScnPlannerPPR,
    esWB_ScnPlannerPR, esWB_ScnPlannerPrdPPR, esWB_ScnPlannerPrdPR, esWB_ScnPlannerTrial, esWB_ScnPlannerTrialCNR,
    esWB_ScnPlannerTrialEvt,
    esWB_ScnPlannerTrialPR,
    esWB_Simulation,
    esWB_Simulation_Input,
    esWB_Simulation_Metrics,
    esWB_Simulation_Metrics_CBO_Set,
    esWB_Simulation_Spend_Metrics,
    esWB_Spend_Function,
    esWB_Spend_Function_Input_Relationship,
    esWB_Spend_Function_Relationship,
    esWB_Spend_Function_Relationship_Scope,
    esWB_User
} from "../PA.Core/PAdbSchema";
import { ErrorMessage_restate, ExceptionMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import { ScenarioPlanBO } from "../PA.Simulation.BusinessObjects/ScenarioPlanBO";
import { ISimulationBOCreateLoadOptions, SimulationBO } from "../PA.Simulation.BusinessObjects/SimulationBO";
import { ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { DateUtils } from "../PA.Core/Basic/Dates";
import { TextUtils } from "../PA.Core/Basic/Text";
import { IwbSimulationSpendMetrics } from "../PA.Core/PAdbSchemaRowsIF";

//-- Tools to support conversion from one Simulation App to another
//--
//-- A. Convert CBO Result Event to a SimulationBO     (CBOResultsGrid)
//-- B. Convert multiple Simulations to ScenarioPlanBO (SimulationPlanCalcDialog)
//-- C. Convert trial to ScenarioPlanBO                (AutoPlanner)

interface ITrialCellToEvt {
    wbCustId: number
    wbScnPlannerPrdPId: number
    wbCBOId: number
    wbCBOSetIdCSV: string
    wbCBOSetIds: number[]

    cells: WB_ScenarioPlannerTrialPRRow[]
    trialEvents: WB_ScenarioPlannerTrialEventRow[]
    periodParts: WB_ScenarioPlannerPeriodPartitionRow[]
    dctPeriodPartId: {[id:number]: number}
}

export class SimulationConversionUtils {

//-- A. Convert CBO Result Event to a SimulationBO     (CBOResultsGrid)
    static async createCBOSimulation(
        cboRow: WB_CBORow,
        wbCboSetIds: number[],
        wbModelProfileId: number,
        wbCustId: number,
        startDate: Date,
        endDate: Date,
        options: {
            dbContext?: PAdbContext,
            sName?: string,
            sId?: string,
            sTrialId?: string,
            embeddedInd?: boolean
            wbScnPlanId?: number
            wbTreeFolderId?: number,
            messageProvider?: IMessageProvider,
            progressProvider?: IProgressProvider,
            bSkipSave?: boolean,
            bSkipCalcAfterSave?: boolean,
            spendFlatRateConversion?: {
                // If coming from CBO calc
                wbSimMtIdParent?: number,

                // If coming from Auto Planner Calc
                wbScnPlannerTrialId?: number,
                wbScnPlannerTrialEvtIds?: number[]
            }
        }
    ): Promise<SimulationBO>
    {
        let opts = options || {}
        let mp = opts?.messageProvider
        let pp = opts?.progressProvider
        if(_.isEmpty(mp))
            mp = new MessageProvider()
        if(_.isEmpty(pp))
            pp = new ProgressProvider()

        pp.start();
        let aSimBO: SimulationBO

        try {
            let cbo = cboRow.cif
            let dbContext = opts.dbContext || <PAdbContext>cboRow.dataStore()

            let createOpts: ISimulationBOCreateLoadOptions = {
                sName: opts.sName || cbo.CBO_NAME + " Simulation:" + " #" + (opts.sTrialId || ''),
                wbMasterDataSetId: cbo.WB_MDSET_ID,
                wbSalesOrgId: cbo.WB_SALESORG_ID,
                wbPublishedModelId: cbo.WB_PUBLISHED_MODEL_ID,
                wbModelProfileId: wbModelProfileId,
                wbCustHId: cbo.WB_CUSTH_ID,
                wbPrdHId: cbo.WB_PRDH_ID,
                wbPeriodHId: cbo.WB_PERIODH_ID,
                dbContext: dbContext,
                wbScnPlanId: opts.wbScnPlanId || cbo.WB_SCNPLAN_ID
            }

            aSimBO = new SimulationBO({ dbContext: dbContext })
            await aSimBO.LoadNew(createOpts)

            aSimBO.WB_Simulation.cif.WB_CBO_ID = cbo.WB_CBO_ID
            aSimBO.WB_Simulation.cif.WB_CUST_ID = wbCustId
            aSimBO.WB_Simulation.StartDate = startDate
            aSimBO.WB_Simulation.EndDate = endDate
            aSimBO.WB_Simulation.cif.WB_TREE_FOLDER_ID = opts?.wbTreeFolderId || 0

            if (_.isBoolean(opts.embeddedInd))
                aSimBO.WB_Simulation.cif.EMBEDDED_IND = opts.embeddedInd ? '1' : '0'

            // Get the Products in Product Set to limit scope
            let cboSets = _.filter(cboRow.WB_CBOSetRows, (a) => a.isActive && _.includes(wbCboSetIds, a.cif.WB_CBO_SET_ID))
            let dctSetsBySetType = _.groupBy(cboSets, (r) => r.cif.SET_TYPE)
            let cboProductSet = _.first(dctSetsBySetType['PRODUCT'])
            let dctPrdInScope: { [wbPrdGrpId: number]: number } = {}
            let dctPrdGrpInScope: { [wbPrdGrpId: number]: number } = {}
            _.forEach(cboProductSet?.WB_CBOSetItemRows || [], (item) => {
                if (!item.isActive)
                    return
                if (item.cif.WB_PROD_ID > 0)
                    dctPrdInScope[item.cif.WB_PROD_ID] = item.cif.WB_PROD_ID
                if (item.cif.WB_PRDGRP_ID > 0)
                    dctPrdGrpInScope[item.cif.WB_PRDGRP_ID] = item.cif.WB_PRDGRP_ID
            })

            //copy spend and shipment functions
            for (let idxFnc = 0; idxFnc < cboRow.WB_SpendFunctionRelationshipRows.length; idxFnc++) {

                let aSRow: WB_SpendFunctionRelationshipRow = cboRow.WB_SpendFunctionRelationshipRows[idxFnc]

                if (!aSRow.isActive)
                    continue

                // Check if we need to convert this Spend
                let srcSpendFunction = aSRow.WB_SpendFunctionRow
                let tgtSpendFunction = srcSpendFunction
                let bConvertSpendToRateAmtFunction = false
                let convOpts = opts?.spendFlatRateConversion || {}
                let bHasFlatRateConversionEvtKey = ((convOpts.wbScnPlannerTrialId > 0) && !_.isEmpty(convOpts.wbScnPlannerTrialEvtIds)) || (convOpts.wbSimMtIdParent > 0)
                if (srcSpendFunction.cif.FUNCTION_USAGE == "Complex" && srcSpendFunction.cif.WB_SPEND_FUNCTION_ID_SPECIFIC > 0 && bHasFlatRateConversionEvtKey) {
                    tgtSpendFunction = await aSimBO.dbContext.find(esWB_Spend_Function, srcSpendFunction.cif.WB_SPEND_FUNCTION_ID_SPECIFIC)
                    if (_.isEmpty(tgtSpendFunction)) {
                        tgtSpendFunction = srcSpendFunction
                    } else {
                        bConvertSpendToRateAmtFunction = true
                    }
                }
                let aSimSpendRow = await aSimBO.addSimulationFunction(tgtSpendFunction.cif.WB_SPEND_FUNCTION_ID, {
                    [tgtSpendFunction.cif.WB_SPEND_FUNCTION_ID]: tgtSpendFunction,
                    FunctionType: tgtSpendFunction.cif.FUNCTION_TYPE
                })
                aSimSpendRow.cif.SPEND_NAME = aSRow.cif.SPEND_NAME
                aSimSpendRow.cif.SPEND_ID = aSRow.cif.SPEND_ID
                if (bConvertSpendToRateAmtFunction) {
                    aSimSpendRow.cif.AMOUNT_LOCATION = "Product"
                    aSimSpendRow.cif.RATE_UOM = "ConsumerUnit"
                    aSimSpendRow.cif.REDEMPTION_PCNT = 1
                    let cnd: any = {
                        WB_SPEND_FUNCTIONR_ID: aSRow.cif.WB_SPEND_FUNCTIONR_ID,
                        SUMMARY_LEVEL: "TacticProduct",
                        DELETED_IND: { neq: '1' }
                    }
                    if (convOpts.wbSimMtIdParent > 0) {
                        cnd.WB_CBO_ID = cbo.WB_CBO_ID
                        cnd.WB_SIMMT_ID_PRNT = convOpts.wbSimMtIdParent
                    }
                    if ((convOpts.wbScnPlannerTrialId > 0) && !_.isEmpty(convOpts.wbScnPlannerTrialEvtIds)) {
                        cnd.WB_SCNPLANNERTRIAL_ID =  convOpts.wbScnPlannerTrialId
                        cnd.WB_SCNPLANNERTRIALEVT_ID = {inq: convOpts.wbScnPlannerTrialEvtIds}
                    }

                    let arrSummaryRecords = <WB_SimulationSpendMetricsRow[]>await aSimBO.dbContext.findAll(esWB_Simulation_Spend_Metrics, {
                        where: cnd
                    })

                    function summarizeSimSMtBy(records: WB_SimulationSpendMetricsRow[], keyField: string): {[keyId: number]: IwbSimulationSpendMetrics} {
                        let rv: {[keyId: number]: IwbSimulationSpendMetrics} = {}
                        let sSimSmt_MetricFields = ['VOLUME_BASIS_QTY', 'SPEND_AMT',
                                                           'CNS_TOTAL_LISTSLS','CNS_TOTAL_NETLISTSLS','CNS_TOTAL_UNITS',
                                                           'SHIP_LISTSLS','SHIP_NETLISTSLS','SHIP_UNITS']
                        _.forEach(records, (row) => {
                            //--
                            let keyId = +(<any>row)[keyField]
                            if(keyId in rv){
                                let m = rv[keyId]
                                _.forEach(sSimSmt_MetricFields, (mFld) => {
                                    m[mFld] += +(<any>row)[mFld]
                                })
                            } else {
                                let m: IwbSimulationSpendMetrics = {[keyField]: keyId}
                                m.keyId = keyId
                                _.forEach(sSimSmt_MetricFields, (mFld) => {
                                    m[mFld] = +(<any>row)[mFld]
                                })
                                rv[keyId] = m
                            }
                        })

                        return rv
                    }

                    let dctSpndMtrcByPrd = summarizeSimSMtBy(_.filter(arrSummaryRecords, (a) => a.cif.WB_PROD_ID > 0),'WB_PROD_ID')
                    let dctSpndMtrcByPrdGrp = summarizeSimSMtBy(_.filter(arrSummaryRecords, (a) => a.cif.WB_PRDGRP_ID > 0), 'WB_PRDGRP_ID')

                    let bIsVariable = (tgtSpendFunction.cif.FUNCTION_SUBTYPE != 'Fixed')
                    for (let wbProdId in dctSpndMtrcByPrd) {
                        let summary = dctSpndMtrcByPrd[wbProdId]
                        let newPrdRow = await aSimBO.addSimulationFunctionSubModels(
                            aSimSpendRow,
                            0,
                            +wbProdId,
                            0,
                            {}
                        )

                        if (bIsVariable) {
                            if (summary.CNS_TOTAL_UNITS > 0)
                                newPrdRow.cif.RATE_AMT = summary.SPEND_AMT / summary.CNS_TOTAL_UNITS
                        } else {
                            newPrdRow.cif.RATE_AMT = summary.SPEND_AMT
                        }
                    }

                    for (let wbPrdGrpId in dctSpndMtrcByPrdGrp) {
                        let summary = dctSpndMtrcByPrdGrp[wbPrdGrpId]
                        let newPrdGrpRow = await aSimBO.addSimulationFunctionSubModels(
                            aSimSpendRow,
                            0,
                            0,
                            +wbPrdGrpId,
                            {}
                        )

                        if (bIsVariable) {
                            if (summary.CNS_TOTAL_UNITS > 0)
                                newPrdGrpRow.cif.RATE_AMT = summary.SPEND_AMT / summary.CNS_TOTAL_UNITS
                        } else {
                            newPrdGrpRow.cif.RATE_AMT = summary.SPEND_AMT
                        }
                    }


                } else {
                    aSimSpendRow.cif.AMOUNT_LOCATION = "Header"
                    aSimSpendRow.cif.INPUT_VALUE_1 = aSRow.cif.INPUT_VALUE_1
                    aSimSpendRow.cif.INPUT_VALUE_3 = aSRow.cif.INPUT_VALUE_2
                    aSimSpendRow.cif.INPUT_VALUE_3 = aSRow.cif.INPUT_VALUE_3
                    aSimSpendRow.cif.INPUT_VALUE_4 = aSRow.cif.INPUT_VALUE_4

                    for (let i = 0; i < aSRow.WB_SpendFunctionRelationshipScopeRows.length; i++) {
                        let aScopeRow = aSRow.WB_SpendFunctionRelationshipScopeRows[i]
                        if (!aScopeRow.isActive)
                            continue
                        if ((!(aScopeRow.cif.WB_PROD_ID in dctPrdInScope)) &&
                            (!(aScopeRow.cif.WB_PRDGRP_ID in dctPrdGrpInScope)))
                            continue

                        await aSimBO.addSimulationFunctionSubModels(
                            aSimSpendRow,
                            aScopeRow.cif.WB_CUST_ID,
                            aScopeRow.cif.WB_PROD_ID,
                            aScopeRow.cif.WB_PRDGRP_ID,
                            {}
                        )

                    }
                }

            }

            //copy only products in promo
            let prdIds: number[] = _.values(dctPrdGrpInScope)
            await aSimBO.addProductGroups(prdIds)

            await aSimBO.Sync_PredictorsAndInputs()

            //copy predictor values
            let simPRRows: WB_SimulationPredictorRelationshipRow[] =
                aSimBO.WB_Simulation.WB_SimulationPredictorRelationshipRows

            //Step 1: Get the Model Profile Groups
            let profileGroups = _.filter(cboRow.WB_ModelProfileRow.WB_ModelProfileGroupRows, (aRow) => aRow.isActive)

            //Step 2: For every profile group - get the Predictors
            //Step 3: For every predictor - get the value from the Predictor Set
            let dctPredSetsByGrp = _.keyBy(dctSetsBySetType['PREDICTOR'] || [], (sr) => sr.cif.WB_MODEL_PROFILEGR_ID)

            _.forEach(profileGroups, (aGrp) => {
                let setRow: WB_CBOSetRow = dctPredSetsByGrp[aGrp.cif.WB_MODEL_PROFILEGR_ID]
                let setPredictors = _.filter(setRow.WB_CBOSetPredictorRows, (aRow) => aRow.isActive)

                let grpPredictors = _.filter(aGrp.WB_ModelProfileGroupPredictorRows, (aPred) => aPred.isActive)
                _.forEach(simPRRows, (aSimPRRow) => {
                    let itemRow: WB_CBOSetPredictorRow = undefined
                    let wbCustId_Filter = setRow.cif.CUST_IND == "1" ? wbCustId : 0

                    if (setRow.cif.PRD_IND == "1") {
                        itemRow = _.first(
                            _.filter(
                                setPredictors,
                                (aItem) =>
                                    aItem.cif.WB_PRDGRP_ID == aSimPRRow.cif.WB_PRDGRP_ID &&
                                    aItem.cif.WB_CUST_ID == wbCustId_Filter
                            )
                        )
                    } else {
                        itemRow = _.first(_.filter(setPredictors))
                    }

                    if (!_.isUndefined(itemRow)) {
                        _.forEach(grpPredictors, (aPred) => {
                            let predictorID = aPred.WB_ModelPredictorRow.cif.MODEL_PREDICTOR_ID
                            // @ts-expect-error "dynamic column reference"
                            aSimPRRow[predictorID] = itemRow[predictorID]
                        })
                    }
                })
            })

            if (!opts.bSkipSave) {
                await aSimBO.Save()
                if (!aSimBO.bIsSaved) {
                    ErrorMessage_restate(mp, true, 'SaveFailed', `Simulation failed for ${aSimBO.WB_Simulation.cif.SIMULATION_ID}`)
                    mp.addMessages(aSimBO.ValidateMessageProvider.Messages)
                }
            }

            if (aSimBO.bIsSaved && !opts.bSkipSave) {
                await aSimBO.SimulationCalculator.Calculate({
                    WB_SIMULATION_ID: aSimBO.WB_Simulation.cif.WB_SIMULATION_ID
                })
                if (aSimBO.SimulationCalculator.CalcMessageProvider.countMessages(MessageType.Error) != 0) {
                    mp.addMessages(aSimBO.SimulationCalculator.CalcMessageProvider.Messages)
                }

            }
            pp.finish()
        } catch(err) {
            ExceptionMessage_restate(mp, err)
            pp.exception(err)
        } finally {
            //
        }

        return aSimBO
    }

    static async createScenarioPlanFromSims(wbScnPlanIdTemplate: number,
                                            wbSimIds: number[],
                                            options?: {
                                                sName?: string,
                                                wbTreeFolderId?: number,
                                                messageProvider?: IMessageProvider,
                                                progressProvider?: IProgressProvider,
                                            }): Promise<ScenarioPlanBO>
    {
        let opts = options || {}
        let mp = opts?.messageProvider
        let pp = opts?.progressProvider
        if (_.isEmpty(mp))
            mp = new MessageProvider()
        if (_.isEmpty(pp))
            pp = new ProgressProvider()

        pp.start();
        let newScenario: ScenarioPlanBO
        try {
            // Load Scenario BO, Create Copy, Save
            let progressIdx = 1
            let progressLength = 2 + wbSimIds.length
            pp.setProgressDenominator(progressLength)
            pp.setProgressNumerator(progressIdx)

            pp.setProgressMessage("lt", "Loading Scenario Template")
            let scnTemplate = new ScenarioPlanBO()
            await scnTemplate.Load(wbScnPlanIdTemplate)
            pp.setProgressMessage("lt", "Create Template Copy")
            newScenario = await scnTemplate.Copy({
                bSkipShipmentFunctions: true,
                bSkipSpendFunctions: true,
                bEventMode: true
            })

            pp.setProgressMessage("lt", "Saving Scenario Plan")
            if (!_.isEmpty(opts.sName))
                newScenario.WB_ScenarioPlan.cif.PLAN_NAME = opts.sName
            await newScenario.Save()


            pp.setProgressMessage("lt", "Creating Scenario Plan Events")
            // Create Simulations
            let idx = 1
            progressIdx++

            for (let wbSimulationId of wbSimIds) {

                pp.setProgressNumerator(progressIdx)
                pp.setProgressMessage("lt", `Loading Source Event ${idx}/ ${wbSimIds.length}`)
                let simBO = new SimulationBO({})
                await simBO.Load(wbSimulationId)
                pp.setProgressMessage("lt", `Copying Source Event ${idx}/ ${wbSimIds.length}`)
                let newSimBO = <SimulationBO>await simBO.Copy({})
                newSimBO.WB_Simulation.cif.WB_SCNPLAN_ID = newScenario.WB_ScenarioPlan.cif.WB_SCNPLAN_ID
                newSimBO.WB_Simulation.cif.EMBEDDED_IND = '1'
                pp.setProgressMessage("lt", `Saving Event ${idx}/ ${wbSimIds.length}`)
                await newSimBO.Save()
                if(!newSimBO.bIsSaved) {
                    ErrorMessage_restate(mp, true, 'SaveFailed', `Failed to Save Event ${newSimBO.WB_Simulation.cif.SIMULATION_ID}`)
                    mp.addMessages(newSimBO.ValidateMessageProvider.Messages)
                }
                idx++
                progressIdx++
            }
            pp.finish()
        } catch (err) {
            pp.exception(err)
            ExceptionMessage_restate(mp, err)
        }
        return newScenario
    }

    static async createScenarioPlansFromTrialIds(wbScnPlannerId: number,
                                                 wbScnPlannerTrialIds: number[],
                                                 options?: {
                                                     wbTreeFolderId?: number,
                                                     messageProvider?: IMessageProvider,
                                                     progressProvider?: IProgressProvider,
                                                     subProgressProvider?: IProgressProvider,
                                                     dbContext?: PAdbContext
                                                 }): Promise<ScenarioPlanBO[]>
    {
        const rv: ScenarioPlanBO[] = []

        let dbContext = options?.dbContext
        if(_.isEmpty(dbContext))
            dbContext = new PAdbContext()

        let pp = options?.progressProvider || new ProgressProvider()
        let ppSub = options?.subProgressProvider || new ProgressProvider()
        let mp = options?.messageProvider || new MessageProvider()
        let wbTreeFolderId = options?.wbTreeFolderId || 0

        let rrPlanner = new ESRecordResolver(dbContext, {
            standardConditions: {
                [esWB_ScnPlanner]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerCR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerPR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerCnstr]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerPPR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerPrdPR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerPrdPPR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerESR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerTrial]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerTrialCNR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerTrialPR]: { "DELETED_IND": { "notIn": ['1'] } },
                [esWB_ScnPlannerTrialEvt]: { "DELETED_IND": { "notIn": ['1'] } },

            },
            dctIncludeBelongsToNamedRelations: {
                [esWB_Published_Model]: [esWB_Entity, esWB_Model],
            },
            dctExcludeHasManyNamedRelations: {
                [esWB_ScnPlanner]: [esWB_ScnPlannerCalc,  esWB_ScnPlannerTrial, esWB_ScnPlannerTrialEvt]
            }
        })
        let scnPlannerRow = <WB_ScenarioPlannerRow> _.first(await rrPlanner.findAllWithRelationships(esWB_ScnPlanner, [wbScnPlannerId]));

        let rrTrials = new ESRecordResolver(dbContext, {
            dctExcludeHasManyNamedRelations: {
                [esWB_ScnPlannerTrial]: [esWB_ScnPlannerTrialCNR]
            }
        })
        let trialRows = <WB_ScenarioPlannerTrialRow[]> await rrTrials.findAllWithRelationships(esWB_ScnPlannerTrial, wbScnPlannerTrialIds);
        trialRows = _.filter(trialRows, (t) => t.cif.WB_SCNPLANNERTRIAL_ID == wbScnPlannerId);

        if (_.isEmpty(trialRows))
            return

        pp.start();
        pp.setProgressMessage('CreatingTrials', 'Creating Trials');
        pp.setProgressDenominator(trialRows.length);
        let progressIdx = 0

        for (let trial of trialRows) {
            pp.setProgressNumerator(progressIdx)
            let bo = await SimulationConversionUtils.createScenarioPlanFromTrial(scnPlannerRow, trial, {
                wbTreeFolderId: wbTreeFolderId,
                dbContext: dbContext,
                sName: `Trial ${trial.cif.TRIAL_ID}`,
                messageProvider: mp,
                progressProvider: ppSub
            })

            rv.push(bo)
            progressIdx++
        }

        pp.finish()

        return rv
    }

    static async createScenarioPlanFromTrial(scnPlannerRow: WB_ScenarioPlannerRow,
                                             trialRow: WB_ScenarioPlannerTrialRow,
                                             options?: {
                                                 sName?: string,
                                                 wbTreeFolderId?: number,
                                                 messageProvider?: IMessageProvider,
                                                 progressProvider?: IProgressProvider,
                                                 dbContext?: PAdbContext
                                             }): Promise<ScenarioPlanBO>
    {
        let opts = options || {}
        let mp = opts?.messageProvider
        let pp = opts?.progressProvider
        if (_.isEmpty(mp))
            mp = new MessageProvider()
        if (_.isEmpty(pp))
            pp = new ProgressProvider()

        pp.start();
        let dbContext = opts.dbContext || <PAdbContext> scnPlannerRow.dataStore()
        let newScenario: ScenarioPlanBO

        let progressLength = 3
        try {
            // A. Create Shell
            // B. Copy Original Simulations that fall below Optimization Scope
            // C. Find all trial Events, consolidate consecutive events,
            // -- same cbo/ cboSets
            // -- same product partition
            // -- consecutive periods
            // D. Create Simulations for consolidated trials
            let scnPlanRow: WB_ScenarioPlanRow = await dbContext.find(esWB_ScnPlan, scnPlannerRow.cif.WB_SCNPLAN_ID)

            let wbScnPlanIdTemplate = scnPlanRow.cif.WB_SCNPLAN_ID

            let progressIdx = 1
            let progressLength = 5
            // A. Create Shell
            pp.setProgressDenominator(progressLength)
            pp.setProgressNumerator(progressIdx)
            pp.setProgressMessage("lt", "Loading Template")
            let scnTemplate = new ScenarioPlanBO()
            await scnTemplate.Load(wbScnPlanIdTemplate)
            progressIdx += 1
            pp.setProgressNumerator(progressIdx)
            pp.setProgressMessage("ct", "Creating Copy")
            newScenario = await scnTemplate.Copy({
                bSkipShipmentFunctions: true,
                bSkipSpendFunctions: true,
                bEventMode: true,
            })

            progressIdx += 1
            pp.setProgressNumerator(progressIdx)
            pp.setProgressMessage("ss", "Saving Plan")
            if(!_.isEmpty(opts.sName))
                newScenario.WB_ScenarioPlan.cif.PLAN_NAME = opts.sName
            if (opts.wbTreeFolderId > 0)
                newScenario.WB_ScenarioPlan.cif.WB_TREE_FOLDER_ID = opts.wbTreeFolderId
            await newScenario.Save({})


            // B. Copy Original Simulations that fall below Optimization Scope
            // Read in Simulations from Original Template that are below scenario plan's optimization level
            let wbModelId = scnPlannerRow.WB_PublishedModelRow.cif.WB_MODEL_ID
            let wbModelEvtTypeRows = <WB_ModelEventTypeRelationshipRow[]> await dbContext.findAll(esWB_Model_Event_Type_Relationship, {where: {WB_MODEL_ID: wbModelId, DELETED_IND: {neq: '1'} }})
            let wbModelVolCompRows = <WB_ModelVolumeComponentRelationshipRow[]> await dbContext.findAll(esWB_Model_Volume_ComponentR, {where: {WB_MODEL_ID: wbModelId, DELETED_IND: {neq: '1'} }})
            let dctVolCompRByWbId = _.keyBy(wbModelVolCompRows, (a) => a.cif.WB_MODEL_VOLUME_COMPONENT_ID)
            let optimizationEvtType = _.first(_.filter(wbModelEvtTypeRows, (evt) => evt.cif.WB_MODEL_EVTTYPER_ID == scnPlannerRow.cif.WB_MODEL_EVTTYPER_ID))
            let optimizationVolCompId = optimizationEvtType?.cif?.WB_MODEL_VOLUME_COMPONENT_ID || -1

            progressIdx += 1
            pp.setProgressDenominator(progressLength)
            pp.setProgressNumerator(progressIdx)

            if ((!_.isEmpty(optimizationEvtType)) && (optimizationVolCompId in dctVolCompRByWbId)) {
                pp.setProgressMessage("ste", "Creating Scenario Plan Events")
                // Create Simulations
                let idx = 1
                progressIdx++

                let optDecompOrder = dctVolCompRByWbId[optimizationVolCompId]?.cif?.DECOMPOSITION_ORDER || 0
                let lowerVolDecompwbVCIds = _.map(_.filter(wbModelVolCompRows, (a) => a.cif.DECOMPOSITION_ORDER < optDecompOrder), (b) => b.cif.WB_MODEL_VOLUME_COMPONENT_ID)
                let copyMdlEvtTypes = _.filter(wbModelEvtTypeRows, (r) => _.includes(lowerVolDecompwbVCIds, r.cif.WB_MODEL_VOLUME_COMPONENT_ID))
                let copyMdlEventTypeWbIds = _.map(copyMdlEvtTypes, (a) => a.cif.WB_MODEL_EVTTYPER_ID)

                let arrSimulations = <WB_SimulationRow[]>await newScenario.dbContext.findAll(esWB_Simulation, {
                    where: {
                        WB_SCNPLAN_ID: wbScnPlanIdTemplate,
                        EMBEDDED_IND: '1'
                    }
                }, { with: [esWB_Model_Profile, esWB_Published_Model] })
                let copySimulations = _.filter(arrSimulations, (s) => _.includes(copyMdlEventTypeWbIds, s.WB_ModelProfileRow?.cif?.WB_MODEL_VOLUME_COMPONENT_ID))

                progressLength += copySimulations.length
                pp.setProgressDenominator(progressLength)
                let wbSimIds = _.map(copySimulations, (a) => a.cif.WB_SIMULATION_ID)

                for (let wbSimulationId of wbSimIds) {

                    pp.setProgressNumerator(progressIdx)
                    pp.setProgressMessage("lt", `Loading Source Event ${idx}/ ${wbSimIds.length}`)
                    let simBO = new SimulationBO({})
                    await simBO.Load(wbSimulationId)
                    pp.setProgressMessage("lt", `Copying Source Event ${idx}/ ${wbSimIds.length}`)
                    let newSimBO = <SimulationBO>await simBO.Copy({})
                    newSimBO.WB_Simulation.cif.WB_SCNPLAN_ID = newScenario.WB_ScenarioPlan.cif.WB_SCNPLAN_ID
                    newSimBO.WB_Simulation.cif.EMBEDDED_IND = '1'
                    pp.setProgressMessage("lt", `Saving Event ${idx}/ ${wbSimIds.length}`)
                    await newSimBO.Save()
                    if(!newSimBO.bIsSaved) {
                        ErrorMessage_restate(mp, true, 'SaveFailed', `Failed to Save Event ${newSimBO.WB_Simulation.cif.SIMULATION_ID}`)
                        mp.addMessages(newSimBO.ValidateMessageProvider.Messages)
                    }
                    idx++
                    progressIdx++
                }

            } // Internal Events

            // C. Find all trial Events, consolidate consecutive events,
            // -- same cbo/ cboSets
            // -- same product partition
            // -- consecutive periods
            let arrPromoToCreate: ITrialCellToEvt[] = []
            let dctEvtByPrdPeriodId: {[wbPrdPartId: number]: {[periodPartId: number]: ITrialCellToEvt}} = {}
            let arrPromoParts = _.filter(trialRow.WB_ScenarioPlannerTrialPRRows, (a) => a.isActive && (a.cif.PROMOTED_IND == '1'))
            let dctByPartition = _.groupBy(arrPromoParts, (a) => a.cif.WB_SCNPLANNERPRDPR_ID)
            let wbTrialEvtIds = _.map(arrPromoParts, (a) => a.cif.WB_SCNPLANNERTRIALEVT_ID_CHOSEN)
            wbTrialEvtIds = _.filter(wbTrialEvtIds, (evtId) => evtId > 0)
            const arrEvts = <WB_ScenarioPlannerTrialEventRow[]> await dbContext.findAll(esWB_ScnPlannerTrialEvt, wbTrialEvtIds)
            const dctEvts = _.keyBy(arrEvts, (e) => e.cif.WB_SCNPLANNERTRIALEVT_ID)
            const arrPeriodPartIds = _.filter(scnPlannerRow.WB_ScenarioPlannerPeriodPartitionRows, (a) => a.isActive)
            const dctPeriodPartIds = _.keyBy(arrPeriodPartIds, (pp) => pp.cif.PERIOD_PARTITION_ID)

            for (const wbScnPlannerPrdPrIdIdx in dctByPartition) {
                let wbScnPlannerPrdPrId = +wbScnPlannerPrdPrIdIdx
                let cells = dctByPartition[wbScnPlannerPrdPrId]
                cells = _.sortBy(cells, ['PERIOD_PARTITION_ID'])
                dctEvtByPrdPeriodId[wbScnPlannerPrdPrId] = {}
                let dctEvtByPeriodId = dctEvtByPrdPeriodId[wbScnPlannerPrdPrId]

                for(let cell of cells){
                    let periodPartId = cell.cif.PERIOD_PARTITION_ID
                    let prevPeriodPartId = periodPartId - 1
                    let wbTrialEvtId = cell.cif.WB_SCNPLANNERTRIALEVT_ID_CHOSEN
                    let evt = dctEvts[wbTrialEvtId]
                    let perdPart = dctPeriodPartIds[periodPartId]
                    if(_.isEmpty(evt) || _.isEmpty(perdPart))
                        continue

                    // Check if previous Period has same specs
                    if(prevPeriodPartId in dctEvtByPeriodId){
                        let prev = dctEvtByPeriodId[prevPeriodPartId]
                        if((prev.wbCBOId == evt.cif.WB_CBO_ID)
                            && (prev.wbCBOSetIdCSV == evt.cif.WB_CBO_SET_ID_CSV))
                        {
                            // Update existing
                            prev.cells.push(cell)
                            prev.trialEvents.push(evt)
                            prev.periodParts.push(perdPart)
                            prev.dctPeriodPartId[periodPartId] = periodPartId
                            dctEvtByPeriodId[periodPartId] = prev
                            continue
                        }
                    }

                    // Add Trial Event
                    dctEvtByPeriodId[periodPartId] = {
                        wbCustId: cell.cif.WB_CUST_ID,
                        wbScnPlannerPrdPId: wbScnPlannerPrdPrId,
                        wbCBOId: evt.cif.WB_CBO_ID,
                        wbCBOSetIdCSV: evt.cif.WB_CBO_SET_ID_CSV,
                        wbCBOSetIds: _.map(evt.cif.WB_CBO_SET_ID_CSV.split(","), (n) => +n),

                        cells: [cell],
                        trialEvents: [evt],
                        periodParts: [perdPart],
                        dctPeriodPartId: {[periodPartId]: periodPartId}
                    }
                    arrPromoToCreate.push(dctEvtByPeriodId[periodPartId])

                } // Next Cell

            } // Next Partition

            // Load CBOs to create Partitions
            let wbCBOIds = _.uniq(_.map(arrPromoToCreate, (p) => p.wbCBOId))
            pp.setProgressMessage("LoadingPromotionSets", `Loading ${wbCBOIds.length} Promotion Set(s)`)

            let rr = new ESRecordResolver(dbContext, {
                standardConditions: {
                    [esWB_Simulation_Input]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_Published_Model]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO_Input_Relationship]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO_Constraint]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO_Set]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO_Set_Item]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_CBO_Set_Predictor]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_Spend_Function_Relationship]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_Spend_Function_Relationship_Scope]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_Simulation_Metrics]: { DELETED_IND: { notIn: ["1"], SUMMARY_LEVEL: "Customer" } },
                    [esWB_Simulation_Metrics_CBO_Set]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_Model_Profile_Group]: { DELETED_IND: { notIn: ["1"] } },
                    [esWB_ScnPlannerPR]: { DELETED_IND: { notIn: ["1"] } },
                },
                dctIncludeBelongsToNamedRelations: {
                    [esWB_Published_Model]: [esWB_User],
                    [esWB_Spend_Function]: [esWB_Spend_Function_Input_Relationship],
                    [esWB_Model_Profile_Group]: [esWB_Model_Profile_Group_Predictor],
                    [esWB_Model_Profile_Group_Predictor]: [esWB_Model_Predictor],
                    [esWB_ScnPlanner]: [esWB_ScnPlannerPR]
                },
                dctExcludeHasManyNamedRelations: {
                    [esWB_CBO]: [esWB_Simulation_Metrics]
                }
            })

            const cbos = <WB_CBORow[]> await rr.findAllWithRelationships(esWB_CBO, wbCBOIds)
            let dctCBO = _.keyBy(cbos, (c) => c.cif.WB_CBO_ID)

            // Create Promotions
            progressLength += arrPromoToCreate.length
            pp.setProgressDenominator(progressLength)
            pp.setProgressMessage("CreatingEvents", `Creating ${arrPromoToCreate.length} trial event(s)`)

            for(let prm of arrPromoToCreate){
                let cbo = dctCBO[prm.wbCBOId]
                let startDt: Date
                let endDt: Date
                pp.setProgressNumerator(progressIdx)
                let wbModelProfileId = cbo.WB_ModelProfileRow.cif.WB_MODEL_PROFILE_ID_PRM

                if(wbModelProfileId == 0){
                    ErrorMessage_restate(mp, true, 'NoCBOPrmModelProfile', `Promotion Simulator Profile not set for Optimizer Profile ${cbo.WB_ModelProfileRow?.cif?.PROFILE_NAME}`)
                    continue
                }

                _.forEach(prm.periodParts, (pp) => {
                    startDt = DateUtils.Least(startDt, pp.StartDt)
                    endDt = DateUtils.Greatest(endDt, pp.EndDt)
                })
                let evt = _.first(prm.trialEvents)
                let evtId = TextUtils.padStart(evt.cif.WB_SCNPLANNERTRIALEVT_ID.toString(36),12,'0').toUpperCase()
                let sName = cbo.cif.CBO_NAME + ', ' + evtId

                let newSim = await SimulationConversionUtils.createCBOSimulation(cbo, prm.wbCBOSetIds,
                    wbModelProfileId, prm.wbCustId, startDt, endDt, {
                        dbContext: dbContext,
                        sName: sName,
                        embeddedInd: true,
                        wbScnPlanId: newScenario.WB_ScenarioPlan.cif.WB_SCNPLAN_ID,
                        bSkipCalcAfterSave: true,
                        spendFlatRateConversion: {
                            wbScnPlannerTrialId: trialRow.cif.WB_SCNPLANNERTRIAL_ID,
                            wbScnPlannerTrialEvtIds: _.map(prm.trialEvents, (evt) => evt.cif.WB_SCNPLANNERTRIALEVT_ID)
                        }
                    })

                if (newSim.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    ErrorMessage_restate(mp, true, 'ValidateErrors-' + evtId, 'Errors when Saving: ' + sName)
                    mp.addMessages(newSim.ValidateMessageProvider.Messages)
                }

                progressIdx++

            } // Next Promotion

            pp.finish()
        } catch (err) {
            pp.exception(err)
            ExceptionMessage_restate(mp, err)
        }

        return newScenario
    }

}